import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { TableRow, TableCell } from '@material-ui/core';
import classNames from 'classnames';

import './TournamentTable.scss';
import { RootState } from '../../../configs/store';
import SportContext from '../Context/Sport.context';
import { getFlag } from '../../../utils/utils';
import Icon from '../../Icon/Icon';
import MainMarketHeaders from '../MainMarketHeaders/MainMarketHeaders';
import EventRow from '../EventRow/EventRow';
import StatsModal from '../../StatsModal/StatsModal';
import BottomMenuContext from 'src/components/BottomMenu/Context/BottomMenu.context';
import useIsMobile from 'src/hooks/useIsMobile';

interface TournamentTableProps {
  tournament: {
    code: string,
    meetingInfos?: {
      name: string
    },
    events: {
      eventCode: number,
      eventType: string
    }[]
    name: string,
    linkId: string,
    countryCode: string,
    country: string
  }
}

function TournamentTable({ tournament }: TournamentTableProps) {
  const [collapsed, setCollapsed] = useState(true);
  const { filterSports, isSearching } = useSelector((state: RootState) => state.events);
  const dispatch = useDispatch();
  const history = useHistory();
  const { selectedMainMarkets, screen } = useContext(SportContext);
  const { selectedTab } = useContext(BottomMenuContext);
  const mobile = useIsMobile();

  const firstNumberEvent = tournament.events[0];
  const isNumberEvent = _.has(tournament, 'numberInfos');
  const [toggleModal, setToggleModal] = useState(false);

  useEffect(() => {
    if ((filterSports.level !== 0 || isSearching) && selectedTab === '' && collapsed) {
      setCollapsed(false);
    } else if (!collapsed){
      setCollapsed(true);
    }
  }, [filterSports.level, isSearching, selectedTab]);

  const collapseTournament = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  const openNewWindow = (name, id) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    window.open(
      `https://s5.sir.sportradar.com/keithhobexchange/${ localStorage.getItem('locale') || 'en' }/uniquetournament/${ id }`,
      name,
      'left=0,top=0,width=100,height=100,status,resizable,scrollbars,noopener,noreferrer'
    );
  };

  const openNewModal = (e) => {
    e.stopPropagation();

    setToggleModal(!toggleModal);
  };

  const handleOpenNumberBetting = event => e => {
    e.preventDefault();

    dispatch.events.resetSearchResults();

    history.push(`/lotto/${ event.tournamentCode }/${ event.eventCode }`);
  };

  return (
    <>
      <TableRow
        className={
          classNames({
            'tournament-row__tournament-header': true,
            'tournament-row__tournament-header--closed': collapsed
          })
        }
        onClick={
          !isNumberEvent
            ? collapseTournament
            :  handleOpenNumberBetting(firstNumberEvent)
        }
      >
        <TableCell align="center" style={ { width: '45px' } }>
          <span
            className="tournament-row__tournament-flag"
            style={ { backgroundImage: `url(${ getFlag(tournament.countryCode) })` } }
          ></span>
        </TableCell>

        <TableCell
          align="left"
          colSpan={ screen === 'desktop' ? 2 : 1 }
          className="tournament-row__table-cell-name"
          { ...(isNumberEvent && { onClick: handleOpenNumberBetting(firstNumberEvent) }) }
          onClick={ collapseTournament }
        >
          <span className="tournament-row__tournament-name" title={ tournament.name } >{ `${ tournament.country } - ${ tournament.name }` }</span>

          {
            screen === 'desktop' && !isNumberEvent && tournament.linkId && (
              <Icon
                icon="graph"
                className="tournament-row__tournament-graph"
                onClick={ openNewWindow(tournament.name, tournament.linkId) }
              />
            )
          }
        </TableCell>
        <TableCell align="center" style={ { width: '45px' } } />
        { !mobile && <TableCell align="center" style={ { width: '45px' } } /> }
        {
          screen === 'mobile' && (
            <TableCell>
              <Icon icon="graph" className="tournament-row__tournament-graph" onClick={ openNewModal } />
            </TableCell>
          )
        }

        {
          _.map(selectedMainMarkets, (mainMarket) => (
            <MainMarketHeaders key={ mainMarket.marketTypeCode } mainMarket={ mainMarket } collapsed={ collapsed } />
          ))
        }

        {
          screen === 'desktop' && (
            <>
              <TableCell align="center" style={ { width: '45px' } }>
                <div className="sport-table__event-markets sport-table__event-markets--clear"></div>
              </TableCell>

              <TableCell align="center" style={ { width: '30px' } }>
                {
                  !isNumberEvent && <Icon
                    icon="arrow"
                    className={
                      classNames({
                        'tournament-row__tournament-collapse': true,
                        'tournament-row__tournament-collapse--collapsed': collapsed
                      })
                    }
                  />
                }
              </TableCell>
            </>
          )
        }
      </TableRow>

      {
        (tournament?.meetingInfos && tournament?.meetingInfos.name !== tournament.name) && (
          <TableRow
            className={
              classNames({
                'event-row': true,
                'event-row__meeting--collapsed': collapsed
              })
            }
          >
            <TableCell colSpan={ 99 } className="event-row__meeting">{ tournament.meetingInfos.name }</TableCell>
          </TableRow>
        )
      }
      {
        !isNumberEvent && _.map(tournament.events, (event) => (
          <EventRow
            key={ event.eventCode }
            event={ event }
            tournamentName={ `${ tournament.name }, ${ tournament.country }` }
            tournament={ tournament }
            collapsed={ collapsed }
          />
        ))
      }
      {
        toggleModal &&
          <StatsModal linkId={ tournament?.linkId } toggle={ openNewModal } type="tournament" />
      }
    </>
  );
}

export default React.memo(TournamentTable);
